import type { EntityKind, Identity } from '@/types/identity';
import { IdentityKnownFilter } from '@/types/identity';

interface FilterIdentitiesParams {
    identities: Identity[];
    kind: EntityKind;
    search: string;
    knownFilter: IdentityKnownFilter;
    selectedTags: Set<string>;
}

function matchesSearch(identity: Identity, needle: string): boolean {
    if (needle.length === 0) {
        return true;
    }
    const haystack = [
        identity.name ?? '',
        identity.licensePlate ?? '',
        ...identity.tags,
    ];
    return haystack.some((value) => value.toLowerCase().includes(needle));
}

function matchesKnownFilter(
    identity: Identity,
    knownFilter: IdentityKnownFilter,
): boolean {
    if (knownFilter === IdentityKnownFilter.KNOWN) {
        return identity.known;
    }
    if (knownFilter === IdentityKnownFilter.UNKNOWN) {
        return !identity.known;
    }
    return true;
}

function matchesTags(identity: Identity, selectedTags: Set<string>): boolean {
    if (selectedTags.size === 0) {
        return true;
    }
    return identity.tags.some((tag) => selectedTags.has(tag));
}

export function filterIdentities({
    identities,
    kind,
    search,
    knownFilter,
    selectedTags,
}: FilterIdentitiesParams): Identity[] {
    const needle = search.trim().toLowerCase();
    return identities.filter(
        (identity) =>
            identity.kind === kind &&
            matchesSearch(identity, needle) &&
            matchesKnownFilter(identity, knownFilter) &&
            matchesTags(identity, selectedTags),
    );
}

export function collectIdentityTags(identities: Identity[]): string[] {
    return Array.from(
        new Set(identities.flatMap((identity) => identity.tags)),
    ).sort();
}
